"use client";

import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Calendar } from "@/components/booking/Calendar";
import { PaymentInfoNote } from "@/components/checkout/PaymentInfoNote";
import { VerifonePaymentFrame } from "@/components/checkout/VerifonePaymentFrame";
import { Button, ButtonLink } from "@aluume/shared/components/ui/Button";
import { nzd, nzTime, nzDateLong, nzDateShort, todayNZ, addDays, partySummary, REMARKABLES_PRODUCT_NAME } from "@aluume/shared/lib/format";
import { fmtNZDate } from "@aluume/shared/lib/time";
import { COUNTRY_CODES, splitPhone, combinePhone } from "@aluume/shared/lib/phone";
import { BATHE_CONFIG, THERMAE_CONFIG } from "@aluume/shared/lib/venues";


type Booking = {
  /** Multi-pool booking: every pool on the basket, and the total guests across them. */
  pools?: string[] | null;
  group_guests?: number | null;
  id: string;
  booking_ref: string;
  status: string;
  venue_name: string;
  venue_slug: string;
  resource_name: string;   // physical pool name e.g. "Pool 1"
  resource_id?: string | null;
  resource_type?: string | null;   // 'exclusive' | 'capacity'
  booking_kind?: string | null;     // 'standard' | 'remarkables' (exclusive hire)
  resource_category?: string | null; // e.g. "Canopy", "Mountain View", "Communal"
  start_at: string;
  duration_minutes: number;
  party_adults: number;
  party_children: number;
  total_amount: number | null;
  // Outstanding balance owing (total_amount − payments allocated). A payment-link
  // booking is confirmed UNPAID so this equals the full total; a normally-paid booking
  // is 0/null. Drives the true "amount owing" preview on the Change screen AND the
  // Pay-now banner. `has_pay_token` tells the Change screen a link exists; `pay_token`
  // (below) is the actual link, surfaced only when there's a balance to settle online.
  balance_due?: number | null;
  has_pay_token?: boolean;
  channel?: string | null;
  /** Made by an agent on account — the guest can't change/cancel it here (see notice). */
  agent_booked?: boolean;
  agent_name?: string | null;
  customer_id?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  tier_name?: string | null;
  /** Public pay token — present only when there's a balance to settle online. */
  pay_token?: string | null;
};

type Slot = {
  resource_id: string;
  resource_slug: string;
  resource_name: string;   // category name for private, resource name for communal
  booking_model: string;
  start_at: string;
  seats_left: number | null;
  tier_id: string | null;
  tier_name: string | null;
  price: number | null;
  // Remarkables blocks carry their own duration (the 75-min guest soak) from
  // get_remarkables_availability; standard slots omit it (they use the booking's duration).
  duration_minutes?: number;
};

function StatusPill({ status }: { status: string }) {
  const label = status === "checked_in" ? "Checked In" : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-bg-elevated px-3 py-1.5 text-[10px] tracking-[0.4px] uppercase font-normal text-ink-soft shrink-0">
      <span className="size-1.5 rounded-full bg-ink inline-block" />
      {label}
    </span>
  );
}

function BookingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="ui-label text-ink-mute">{label}</span>
      <span className="text-[13px] font-light text-ink">{value}</span>
    </div>
  );
}

function EditableRow({
  label,
  value,
  type = "text",
  onSave,
}: {
  label: string;
  value: string;
  type?: string;
  onSave: (val: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="ui-label text-ink-mute shrink-0">{label}</span>
        <div className="flex items-center gap-2 min-w-0">
          <input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="border-b border-ink/30 bg-transparent text-[13px] py-0.5 text-ink outline-none focus:border-ink min-w-0 w-full"
          />
          <button
            onClick={async () => {
              setSaving(true);
              await onSave(draft);
              setSaving(false);
              setEditing(false);
            }}
            disabled={saving}
            className="text-[11px] text-corten font-medium whitespace-nowrap disabled:opacity-50"
          >
            {saving ? "…" : "Save"}
          </button>
          <button
            onClick={() => { setDraft(value); setEditing(false); }}
            className="text-[11px] text-ink-mute"
          >✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <span className="ui-label text-ink-mute">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-light text-ink">{value || "—"}</span>
        <button
          onClick={() => { setDraft(value); setEditing(true); }}
          className="text-[10px] font-normal text-ink-mute hover:text-ink transition-colors tracking-wider"
        >Edit</button>
      </div>
    </div>
  );
}

// Phone editor with a separate country-code selector + number field (the stored
// value is "<dial> <number>"). Saving "" clears the phone.
function PhoneEditableRow({ value, onSave }: { value: string; onSave: (val: string) => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const init = splitPhone(value);
  const [country, setCountry] = useState(init.country);
  const [num, setNum] = useState(init.number);
  const [saving, setSaving] = useState(false);
  // The display below reads `value` directly, and the Edit/Cancel handlers
  // re-derive country/number from the current `value` via splitPhone — so after a
  // save (parent now feeds back the canonical DB value) the next edit opens with
  // the freshly-stored number, no stale draft.

  if (editing) {
    return (
      <div className="flex items-center justify-between gap-2">
        <span className="ui-label text-ink-mute shrink-0">Phone</span>
        <div className="flex items-center gap-1.5 min-w-0">
          <select value={country} onChange={(e) => setCountry(e.target.value)}
            className="border-b border-ink/30 bg-transparent text-[13px] py-0.5 text-ink outline-none focus:border-ink shrink-0">
            {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="tel" value={num} onChange={(e) => setNum(e.target.value)}
            placeholder="21 123 4567"
            className="border-b border-ink/30 bg-transparent text-[13px] py-0.5 text-ink outline-none focus:border-ink min-w-0 w-full" />
          <button
            onClick={async () => {
              setSaving(true);
              await onSave(combinePhone(country, num));
              setSaving(false);
              setEditing(false);
            }}
            disabled={saving}
            className="text-[11px] text-corten font-medium whitespace-nowrap disabled:opacity-50"
          >
            {saving ? "…" : "Save"}
          </button>
          <button onClick={() => { const i = splitPhone(value); setCountry(i.country); setNum(i.number); setEditing(false); }}
            className="text-[11px] text-ink-mute">✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <span className="ui-label text-ink-mute">Phone</span>
      <div className="flex items-center gap-2">
        <span className="text-[13px] font-light text-ink">{value || "—"}</span>
        <button
          onClick={() => { const i = splitPhone(value); setCountry(i.country); setNum(i.number); setEditing(true); }}
          className="text-[10px] font-normal text-ink-mute hover:text-ink transition-colors tracking-wider"
        >Edit</button>
      </div>
    </div>
  );
}

function Stepper({
  label, value, min, max,
  onChange,
}: { label: string; value: number; min: number; max?: number; onChange: (delta: number) => void }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <span className="text-[10px] tracking-[2px] uppercase font-normal text-mocha">{label}</span>
      <div className="flex items-center gap-3.5">
        <button onClick={() => onChange(-1)} disabled={value <= min}
          className="flex size-8 items-center justify-center rounded-full bg-cream-deep text-[16px] font-light text-ink-soft disabled:opacity-40">−</button>
        <span className="text-[16px] font-light text-ink w-4 text-center">{value}</span>
        <button onClick={() => onChange(1)} disabled={max != null && value >= max}
          className="flex size-8 items-center justify-center rounded-full bg-cream-deep text-[16px] font-light text-ink-soft disabled:opacity-40">+</button>
      </div>
    </div>
  );
}

function nzDateFrom(iso: string) {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Pacific/Auckland" });
}

// Format a YYYY-MM-DD for the date chips. Uses fmtNZDate (anchors at noon *NZ*,
// not noon UTC) so the label matches the YMD — anchoring at noon UTC pushed the
// label a day forward in NZ (+12/+13).
const fmtChip = (ymd: string, opts: Intl.DateTimeFormatOptions) => fmtNZDate(ymd, opts);
const fmtChipWeekday = (d: string) => fmtChip(d, { weekday: "short" });
const fmtChipDay = (d: string) => fmtChip(d, { day: "numeric" });

function ManageInner() {
  const sp = useSearchParams();

  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState<Booking | null>(null);
  const [cancelled, setCancelled] = useState<number | null>(null);
  const [cancelMsg, setCancelMsg] = useState("");
  const [resent, setResent] = useState(false);
  const [contactError, setContactError] = useState("");

  // Change modal
  const [changeOpen, setChangeOpen] = useState(false);
  const [changeDate, setChangeDate] = useState("");
  const [changeSlots, setChangeSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  // 'communal' = capacity, 'private' = exclusive, null = not yet chosen
  const [selectedType, setSelectedType] = useState<"communal" | "private" | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Whether the change flow is currently TARGETING a whole-pool Remarkables hire. Defaults
  // to the booking's own kind on open; the guest can switch INTO it (pick the Remarkables
  // tile under Private) to upgrade, or back OUT of it to a standard pool. Drives the
  // availability feed (kind=remarkables), the single Guests counter and the whole-hire price.
  const [selectedRemarkables, setSelectedRemarkables] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [changeAdults, setChangeAdults] = useState(1);
  const [changeChildren, setChangeChildren] = useState(0);
  const [changing, setChanging] = useState(false);
  // 7-day chip row for the change modal (centered ±3 on the selected day) + calendar.
  const [changeWeekStart, setChangeWeekStart] = useState(todayNZ());
  const [showChangeCal, setShowChangeCal] = useState(false);
  // Cancel confirmation modal (M6) + post-reschedule money note (M5).
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  // Optional free-text "Reason for cancellation" captured in the cancel modal.
  const [cancelReason, setCancelReason] = useState("");
  const [changeNote, setChangeNote] = useState("");
  // Live session for a price-increase difference (FakeProvider completes
  // inline server-side, so this only arrives with real Verifone creds).
  const [livePay, setLivePay] = useState<
    | { kind: "verifone"; iframeUrl: string; providerRef: string; diffCents: number }
    | null
  >(null);
  const [payError, setPayError] = useState("");
  // True once the difference payment has cleared and we're applying the change — swaps
  // the Verifone form for a brief "confirming…" state so the auto-close reads cleanly.
  const [payConfirming, setPayConfirming] = useState(false);

  // The Remarkables — whole-pool exclusive hire of The Commons. Available at Bathe only
  // (config-gated). A customer can self-reschedule an existing hire AND now UPGRADE a
  // standard/private booking to one (Jeremy 2026-07-15). When the flow targets Remarkables
  // the change modal fetches the afternoon 90-min blocks (get_remarkables_availability),
  // shows a single Guests counter (2–8, no adults/children split) and re-prices at the
  // tier-adjusted whole-hire rate.
  //   • isRemarkables      — the SELECTED (target) kind → drives the picker + the "New" summary.
  //   • origIsRemarkables  — the booking's OWN current kind → drives the "Current" summary.
  const isRemarkables = selectedRemarkables;
  const origIsRemarkables = booking?.booking_kind === "remarkables";
  const remConfig = (booking?.venue_slug === "thermae" ? THERMAE_CONFIG : BATHE_CONFIG).remarkables;
  const REM_MIN_PARTY = 2;
  const REM_MAX_PARTY = 8;

  useEffect(() => {
    const r = sp.get("ref");
    const e = sp.get("email");
    if (r && e) { setRef(r); setEmail(e); doLookup(r, e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function doLookup(r = ref, e = email) {
    if (!r || !e) return;
    setBusy(true);
    setError("");
    setBooking(null);
    setCancelled(null);
    setResent(false);
    try {
      const res = await fetch(`/api/manage?ref=${encodeURIComponent(r)}&email=${encodeURIComponent(e)}`);
      const json = await res.json();
      if (!res.ok) {
        setError(res.status === 429
          ? "Too many attempts — please try again in a few minutes."
          : "We couldn't find a booking with those details.");
        return;
      }
      setBooking(json.booking);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function doCancel() {
    if (!booking) return;
    setConfirmCancelOpen(false);
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/manage/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref, email, reason: cancelReason }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error === "agent_booking_contact_agent"
          ? `This booking was made by ${json.agent_name || "your travel agent"} — please contact them to cancel it.`
          : res.status === 403
          ? "This booking can't be cancelled online. Please call us on +64 3 441 2588."
          : "This booking can't be cancelled. Please call us.");
        return;
      }
      // mode: 'refund' (≥24h, to card) | 'voucher' (<24h, emailed) | 'credit' (fallback)
      setCancelMsg(
        json.mode === "refund"
          ? `Booking cancelled. ${json.amount > 0 ? `${nzd(json.amount)} has been refunded to your card` : "Your refund is being processed"} — it may take a few business days to appear.`
          : json.mode === "voucher"
          ? `Booking cancelled. We've emailed you a voucher${json.amount > 0 ? ` for ${nzd(json.amount)}` : ""} (valid 3 years) to use any time.`
          : `Booking cancelled.${json.credit > 0 ? ` A credit of ${nzd(json.credit)} (valid 12 months) has been added to your account.` : ""}`,
      );
      setCancelled(json.amount ?? json.credit ?? 0);
      setBooking({ ...booking, status: "cancelled" });
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!booking) return;
    setBusy(true);
    try {
      await fetch("/api/manage/resend", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref, email }),
      });
      setResent(true);
    } finally {
      setBusy(false);
    }
  }

  async function updateContact(field: "phone" | "email" | "firstName" | "lastName", value: string) {
    if (!booking) return;
    const body: Record<string, string> = { ref, email };
    if (field === "phone") body.phone = value;
    else if (field === "email") body.newEmail = value;
    else if (field === "firstName") body.firstName = value;
    else body.lastName = value;

    const res = await fetch("/api/manage/update-contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      setContactError(json.error === "Email already in use"
        ? "That email belongs to another account. Please try a different one."
        : json.error === "invalid_email"
        ? (json.message ?? "Enter a valid email address.")
        : "Couldn't save — please try again.");
      return;
    }
    setContactError("");
    // Reflect the canonical stored values the API read back (DB truth), so the
    // on-screen number/name/email always equals what actually persisted. If the
    // response is missing the contact for any reason, re-read from the DB.
    const c = json.contact as
      | { phone: string | null; firstName: string | null; lastName: string | null; email: string | null }
      | undefined;
    if (c) {
      setBooking({
        ...booking,
        customer_phone: c.phone,
        customer_first_name: c.firstName,
        customer_last_name: c.lastName,
        customer_email: c.email ?? booking.customer_email,
      });
      if (c.email) setEmail(c.email);
    } else {
      await doLookup(ref, field === "email" ? value : email);
    }
  }

  // `rem` is passed EXPLICITLY (not read from the isRemarkables closure) so a fetch fired
  // in the same tick as a mode toggle uses the intended kind, not the stale render value.
  async function loadChangeSlots(date: string, adults: number, children: number, isInitialOpen = false, rem = isRemarkables) {
    if (!booking) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const params = new URLSearchParams({
        venue: booking.venue_slug,
        date,
        duration: String(booking.duration_minutes),
        party: String(adults + children),
        // adults/children drive per-pool pax scoping in /api/availability — a
        // category time only shows when a pool that seats this party is free.
        adults: String(adults),
        children: String(children),
        // Exclude THIS booking so its own slot doesn't read as taken — the current
        // time stays selectable (e.g. just adding a guest) and communal seats are
        // counted without it.
        excludeBookingId: booking.id,
      });
      // Remarkables (whole-pool hire) has its OWN availability feed: afternoon back-to-back
      // 90-min blocks (11:30 / 1:00 / 2:30…) offered only when every bucket in the block is
      // free, priced tier-adjusted. `party` (= guests, children always 0) is the only input
      // the feed reads. Standard bookings keep the communal/private category grouping.
      if (rem) params.set("kind", "remarkables");
      const res = await fetch(`/api/availability?${params}`);
      const json = await res.json();
      const slots: Slot[] = json.slots ?? [];
      setChangeSlots(slots);

      // On initial open, auto-select the current booking's time slot if available.
      if (isInitialOpen) {
        // Remarkables feed keys blocks by resource + start; match the current hire's slot
        // (surfaced because it's excluded from availability). An UPGRADE has no current
        // Remarkables slot to match → nothing auto-selects, which is correct.
        // Standard: the API uses resource_name = category for private slots, so match by
        // category name + start time.
        const category = booking.resource_category;
        const matchFn = (s: Slot) =>
          rem
            ? s.start_at === booking.start_at && s.resource_id === booking.resource_id
            : s.start_at === booking.start_at &&
              (category ? s.resource_name === category : s.booking_model === (booking.resource_type === "capacity" ? "capacity" : "exclusive"));
        const match = slots.find(matchFn);
        if (match) setSelectedSlot(match);
      }
    } catch {
      setChangeSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }

  function openChange() {
    if (!booking) return;
    const rem = booking.booking_kind === "remarkables";
    const bookingDate = nzDateFrom(booking.start_at);
    setChangeDate(bookingDate);
    // Centre the 7-day chip row on the current booking date (3 either side),
    // clamped so it never starts before today.
    const ws = addDays(bookingDate, -3);
    setChangeWeekStart(ws < todayNZ() ? todayNZ() : ws);
    setChangeNote("");
    setChangeAdults(booking.party_adults);
    setChangeChildren(booking.party_children ?? 0);
    // Default the flow to the booking's own kind; the guest can switch into/out of it.
    setSelectedRemarkables(rem);
    // A Remarkables hire lives under "Private" so its pool tile + the exit affordance show.
    const type = rem ? "private" : (booking.resource_type === "capacity" ? "communal" : "private");
    setSelectedType(type);
    // For a standard private booking, pre-select its category.
    setSelectedCategory(!rem && booking.resource_type === "exclusive" ? (booking.resource_category ?? null) : null);
    setSelectedSlot(null);
    setError("");
    // Reset the Confirm-button spinner in case a prior change left it set (belt-and-
    // braces alongside the finally in confirmChange) so re-opening is never stuck.
    setChanging(false);
    setChangeOpen(true);
    loadChangeSlots(bookingDate, booking.party_adults, booking.party_children ?? 0, true, rem);
  }

  // Pick a day from the 7-day chip row (no recenter — matches the customer flow).
  function selectChangeDate(d: string) {
    if (d < todayNZ()) return;
    setChangeDate(d);
    setSelectedSlot(null);
    loadChangeSlots(d, changeAdults, changeChildren);
  }
  // Choosing from the full calendar recenters the chip window on that day.
  function recenterChange(d: string) {
    const ws = addDays(d, -3);
    setChangeWeekStart(ws < todayNZ() ? todayNZ() : ws);
    selectChangeDate(d);
  }

  function adjustParty(field: "adults" | "children", delta: number) {
    if (field === "adults") {
      // Remarkables is a single Guests counter bounded 2–8 (the template's min/max party);
      // standard bookings keep the ≥1 adults stepper. Children never applies to a hire.
      const next = isRemarkables
        ? Math.min(REM_MAX_PARTY - changeChildren, Math.max(2, changeAdults + delta))
        : Math.max(1, changeAdults + delta);
      setChangeAdults(next);
      loadChangeSlots(changeDate, next, changeChildren);
    } else {
      const next = Math.min(isRemarkables ? REM_MAX_PARTY - changeAdults : Number.MAX_SAFE_INTEGER, Math.max(0, changeChildren + delta));
      setChangeChildren(next);
      loadChangeSlots(changeDate, changeAdults, next);
    }
  }

  function handleTypeSelect(type: "communal" | "private") {
    setSelectedType(type);
    setSelectedSlot(null);
    setSelectedCategory(null);
    // Switching the standard session type always leaves any Remarkables selection behind.
    setSelectedRemarkables(false);
    // The Commons is adults-only — drop any children picked under "Private" so
    // they aren't carried into the availability query or the reschedule payload.
    if (type === "communal" && changeChildren > 0) {
      setChangeChildren(0);
      loadChangeSlots(changeDate, changeAdults, 0, false, false);
    }
  }

  // Enter Remarkables (whole-pool hire) as the change target — from the Remarkables tile
  // under Private. Keeps the adult/child split within the 8-person cap and
  // fetches the afternoon 90-min block feed.
  function selectRemarkables() {
    const guests = Math.min(REM_MAX_PARTY - changeChildren, Math.max(2, changeAdults));
    setSelectedRemarkables(true);
    setSelectedCategory(null);
    setSelectedSlot(null);
    setChangeAdults(guests);
    loadChangeSlots(changeDate, guests, changeChildren, false, true);
  }

  // Leave Remarkables and return to the standard communal/private picker (the type
  // toggle + category pills reappear once the standard feed reloads).
  function exitRemarkables() {
    setSelectedRemarkables(false);
    setSelectedCategory(null);
    setSelectedSlot(null);
    loadChangeSlots(changeDate, changeAdults, changeChildren, false, false);
  }

  // Slot params for the reschedule call (shared by confirm + the live-payment completion).
  function reschedulePayload() {
    return {
      ref,
      email,
      resourceId: selectedSlot!.resource_id,
      startAt: selectedSlot!.start_at,
      // A Remarkables target uses the block's OWN duration (the 75-min soak the engine
      // requires) — critical for an UPGRADE, whose original booking carries a standard
      // (e.g. 60-min) duration. Standard changes keep the booking's duration.
      durationMinutes: isRemarkables
        ? (selectedSlot!.duration_minutes ?? booking!.duration_minutes)
        : booking!.duration_minutes,
      partyAdults: changeAdults,
      partyChildren: changeChildren,
      // TARGET kind so the route reprices + transitions the booking correctly.
      bookingKind: isRemarkables ? "remarkables" : "standard",
    };
  }

  async function confirmChange() {
    if (!booking || !selectedSlot) return;
    setChanging(true);
    setError("");
    setPayError("");
    setPayConfirming(false);
    // Abort a hung request (~30s) so a slow/unreachable payment gateway surfaces a
    // friendly error instead of spinning the Confirm button forever. This is the
    // primary fix for the "stuck loading" freeze on payment-link bookings; the server
    // also has its own gateway timeout as defence-in-depth.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 30_000);
    try {
      const res = await fetch("/api/manage/reschedule", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(reschedulePayload()),
        signal: controller.signal,
      });
      const json = await res.json();
      if (!res.ok) {
        setError(
          res.status === 403
            ? "Changes aren't available within 24 hours of your current session. Please call us."
            : json.error === "ota_booking_contact_platform"
            ? "This booking was made via a partner — please manage it there."
            : json.error === "agent_booking_contact_agent"
            ? `This booking was made by ${json.agent_name || "your travel agent"} — please contact them to change it.`
            : json.error === "payment_failed"
            ? "The payment couldn't be processed. Please try again."
            : json.error === "payment_unavailable"
            ? "We couldn't reach the payment service. Please try again in a moment."
            : "We couldn't complete the change. Please try again or call us.",
        );
        return;
      }

      // Payment required on a LIVE gateway — hand the Verifone session to the client so
      // the guest enters their card inline; the change applies once the gateway confirms
      // (completeReschedule). For an unpaid payment-link booking diffCents is the FULL
      // outstanding amount, so this collects the whole balance.
      if (json.requiresPayment) {
        setLivePay({ kind: "verifone", iframeUrl: json.iframeUrl, providerRef: json.providerRef, diffCents: json.diffCents });
        return;
      }

      // Applied (cheaper/equal slot, or FakeProvider increase completed inline).
      setChangeOpen(false);
      setChangeNote(
        json.charged
          ? `Change confirmed — ${nzd(json.charged)} charged to your card.`
          : json.refunded
          ? `Change confirmed — ${nzd(json.refunded)} refunded to your card.`
          : "Change confirmed.",
      );
      await doLookup(ref, email);
    } catch (e) {
      setError(
        (e as Error)?.name === "AbortError"
          ? "This is taking longer than expected. Please check your connection and try again."
          : "Something went wrong. Please try again.",
      );
    } finally {
      // Always clear the spinner — the missing success-path reset was the root of the
      // stuck-button freeze. The live-payment modal (if shown) is driven separately.
      clearTimeout(timer);
      setChanging(false);
    }
  }

  // Live-payment completion: gateway confirmed the difference → apply the change.
  async function completeReschedule(providerRef: string) {
    if (!booking || !selectedSlot) return;
    try {
      const res = await fetch("/api/manage/reschedule/complete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...reschedulePayload(), providerRef }),
      });
      const json = await res.json();
      setLivePay(null);
      if (!res.ok) {
        setPayError(
          json.error === "slot_unavailable_refunded"
            ? "That time was taken while you paid — your card was refunded. Please pick another time."
            : "Payment received but we couldn't apply the change. Please contact us.",
        );
        return;
      }
      setChangeOpen(false);
      setChangeNote("Change confirmed — the difference has been paid.");
      await doLookup(ref, email);
    } catch {
      setLivePay(null);
      setPayError("Something went wrong applying your change. Please contact us.");
    }
  }

  // Verifone Checkout: poll payment-status by providerRef. The Checkout SDK form is
  // mounted inline by the shared <VerifonePaymentFrame> (same component + flow as the
  // checkout page); the session is created without a return_url so the form stays put
  // and we detect the outcome here by polling.
  useEffect(() => {
    if (!livePay || livePay.kind !== "verifone") return;
    let cancelled = false;

    // Poll until webhook records the payment or we timeout (~5 min at 3s intervals).
    let attempts = 0;
    const provRef = livePay.providerRef;
    const pollTimer = setInterval(async () => {
      if (cancelled) { clearInterval(pollTimer); return; }
      if (attempts++ >= 100) {
        clearInterval(pollTimer);
        if (!cancelled) setPayError("Payment timed out — please try again or contact us.");
        return;
      }
      try {
        const res = await fetch(`/api/manage/payment-status?providerRef=${provRef}`);
        const json = await res.json();
        if (json.status === "succeeded") {
          clearInterval(pollTimer);
          // Payment cleared — swap to the confirming state and apply the change, which
          // closes the modal and shows the post-change confirmation note.
          if (!cancelled) { setPayConfirming(true); await completeReschedule(provRef); }
        }
        // A decline is NOT terminal — the Verifone form shows its own error inline and
        // lets the guest retry with another card on the same session. Keep the modal
        // mounted and keep polling for a successful retry (mirrors the checkout page).
      } catch { /* network blip — keep polling */ }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePay]);

  const hoursUntil = booking
    ? (new Date(booking.start_at).getTime() - Date.now()) / (1000 * 60 * 60)
    : 0;
  const withinWindow = hoursUntil < 24;
  const isActionable = (booking?.status === "confirmed" || booking?.status === "checked_in") && cancelled == null;

  // 7-day chip window for the change modal + the month label above it.
  const changeDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(changeWeekStart, i)), [changeWeekStart]);
  const changeMonthLabel = useMemo(() => {
    const first = changeDates[0], last = changeDates[changeDates.length - 1];
    const firstMonth = fmtChip(first, { month: "long" });
    const lastMonthYear = fmtChip(last, { month: "long", year: "numeric" });
    return firstMonth === fmtChip(last, { month: "long" }) ? lastMonthYear : `${firstMonth} – ${lastMonthYear}`;
  }, [changeDates]);

  // Slot filtering by type
  const communalSlots = useMemo(() => changeSlots.filter(s => s.booking_model === "capacity"), [changeSlots]);
  const privateSlots = useMemo(() => changeSlots.filter(s => s.booking_model === "exclusive"), [changeSlots]);
  const hasMultipleTypes = communalSlots.length > 0 && privateSlots.length > 0;

  const privateCategories = useMemo(() => {
    const seen = new Set<string>();
    return privateSlots.reduce<string[]>((acc, s) => {
      if (!seen.has(s.resource_name)) { seen.add(s.resource_name); acc.push(s.resource_name); }
      return acc;
    }, []);
  }, [privateSlots]);

  const visibleSlots = useMemo(() => {
    // Remarkables mode: changeSlots IS the whole-pool block feed (a flat list, no
    // communal/private grouping — the blocks come back as booking_model='capacity').
    if (isRemarkables) return changeSlots;
    if (!selectedType) return [];
    if (selectedType === "communal") return communalSlots;
    if (selectedCategory) return privateSlots.filter(s => s.resource_name === selectedCategory);
    // Multiple private categories but none picked yet → gate the times behind a
    // category selection (mirrors the main booking flow). When there's only one
    // category the pills don't render, so we keep showing its times directly.
    return privateCategories.length > 1 ? [] : privateSlots;
  }, [isRemarkables, changeSlots, selectedType, selectedCategory, communalSlots, privateSlots, privateCategories]);

  const tierGroups = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const s of visibleSlots) {
      const tier = s.tier_name ?? "Available";
      if (!map.has(tier)) map.set(tier, []);
      map.get(tier)!.push(s);
    }
    return [...map.entries()];
  }, [visibleSlots]);

  // Complimentary booking ($0 stored total — famil / staff session / 100%-off): keep it
  // free on self-reschedule. Force the new total AND the difference to $0 so no "amount to
  // pay" row shows and the CTA reads "Confirm Change" — the server (isComp) authoritatively
  // keeps it $0 too, so the customer is never sent to the payment step. Mirrors the staff
  // change sheet. A booking that still owes money always has a non-zero stored total, so a
  // $0 total reliably means "this costs nothing".
  const isComp = booking != null && Number(booking.total_amount ?? 0) === 0;
  const newTotal = isComp
    ? 0
    : selectedSlot?.price != null
      ? Math.round(selectedSlot.price * 1.02 * 100) / 100
      : null;
  // What the guest has ALREADY paid = stored total − outstanding balance. For an unpaid
  // payment-link booking balance_due equals the full total, so amountAlreadyPaid is 0 and
  // the amount owing (priceDiff) below is the FULL new total — not a misleading sticker
  // "difference". Falls back to the stored total (treat as paid) when balance_due is
  // unknown, exactly mirroring the server's outstanding = newTotal − amountAlreadyPaid.
  const amountAlreadyPaid = booking?.total_amount != null
    ? (booking.balance_due != null
        ? Math.round((Number(booking.total_amount) - Number(booking.balance_due)) * 100) / 100
        : Number(booking.total_amount))
    : null;
  const priceDiff = isComp
    ? 0
    : (newTotal != null && amountAlreadyPaid != null)
      ? Math.round((newTotal - amountAlreadyPaid) * 100) / 100
      : null;

  // Display labels — category is e.g. "Canopy", resource_name is "Pool 1". A Remarkables
  // booking's resource_category is The Commons' own category ("Communal"), which would
  // mislabel the premium hire, so it uses the product's full customer-facing name (the
  // same label the booking flow shows) instead.
  // "Current" summary → the booking's OWN kind (origIsRemarkables); the "New" summary
  // uses the SELECTED target kind (isRemarkables) inline in the JSX below.
  const categoryLabel = origIsRemarkables
    ? REMARKABLES_PRODUCT_NAME
    : booking?.resource_category ?? (booking?.resource_type === "capacity" ? "Communal" : "Private Pool");
  // A multi-pool booking is one reference over several pools — name them all rather than the one
  // leg the reference happens to hang off.
  const poolLabel = (booking?.pools?.length ?? 0) > 1
    ? booking!.pools!.join(" · ")
    : booking?.resource_name; // "Pool 1", "Pool 3", etc.

  // A Remarkables hire has a single guest count (no adults/children split), so the
  // summary reads "N guests"; every other booking keeps the adults + children summary.
  const partyLabel = (adults: number, children: number, rem: boolean) =>
    rem ? `${adults} guest${adults === 1 ? "" : "s"}` : partySummary(adults, children);

  // Whether the booking's venue allows children at all (Bathe yes, Thermae 16+).
  // The Children stepper only ever shows for private pools at such venues — the
  // Commons is adults-only, so children must never be selectable for communal.
  const venueAllowsChildren =
    (booking?.venue_slug === "thermae" ? THERMAE_CONFIG : BATHE_CONFIG).allowChildren;

  return (
    <div className="flex min-h-full flex-1 flex-col bg-bg">
      <SiteHeader />

      {!booking ? (
        <main className="mx-auto w-full max-w-sm flex-1 px-6 py-20">
          <h3 className="mb-2 text-center">Manage your booking</h3>
          <p className="body mb-8 text-center text-ink-mute">
            Enter your reference and email to view or change your booking.
          </p>
          <div className="flex flex-col gap-5">
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink-mute">Booking reference</span>
              <input
                value={ref}
                onChange={(e) => setRef(e.target.value.toUpperCase())}
                placeholder="AL-1234"
                className="border-b border-ink/30 bg-transparent py-2 uppercase tracking-widest text-ink outline-none focus:border-ink"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="ui-label text-ink-mute">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="border-b border-ink/30 bg-transparent py-2 text-ink outline-none focus:border-ink"
              />
            </label>
            <Button onClick={() => doLookup()} loading={busy} disabled={!ref || !email}>
              {busy ? "Looking up…" : "Find booking"}
            </Button>
          </div>
          {error && <p className="caption mt-6 text-center text-corten">{error}</p>}
        </main>
      ) : (
        <main className="flex flex-1 flex-col items-center px-6 pb-20">
          {/* Hero */}
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <p className="ui-label text-ink-mute">
              Manage Your Booking · #{booking.booking_ref}
            </p>
            <h2
              style={{ fontFamily: "var(--font-display)" }}
              className="text-[48px] font-light leading-none text-ink"
            >
              Your booking
            </h2>
          </div>

          {/* Post-change confirmation note (charge / refund settled) */}
          {changeNote && (
            <div className="mb-5 w-full max-w-[560px] rounded-lg bg-mocha/[0.10] px-5 py-3.5 text-center">
              <p className="text-[13px] font-light text-ink">{changeNote}</p>
            </div>
          )}

          {/* Payment outstanding — a payment-link booking that hasn't been paid yet.
              Sends the guest straight to the secure checkout to settle the balance. */}
          {booking.pay_token && (booking.balance_due ?? 0) > 0
            && (booking.status === "confirmed" || booking.status === "checked_in")
            && cancelled == null && (
            <div className="mb-5 flex w-full max-w-[560px] flex-col items-center gap-3 rounded-lg border border-corten/30 bg-corten/[0.07] px-5 py-4 text-center sm:flex-row sm:justify-between sm:text-left">
              <div className="flex flex-col gap-0.5">
                <p className="text-[14px] font-medium text-ink">Payment outstanding</p>
                <p className="text-[12px] font-light text-ink-mute">
                  {nzd(booking.balance_due ?? 0)} is still to pay for this booking.
                </p>
              </div>
              <ButtonLink href={`/pay/${booking.pay_token}`} className="shrink-0">
                Pay now
              </ButtonLink>
            </div>
          )}

          {/* Card */}
          <div className="w-full max-w-[560px] rounded-lg bg-white p-8 shadow-sm">
            {/* Header: category label + status pill */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-0.5">
                <p
                  style={{ fontFamily: "var(--font-display)" }}
                  className="text-[22px] font-light leading-snug text-ink"
                >
                  {categoryLabel}
                </p>
                <p className="text-[12px] font-light text-ink-mute">
                  {poolLabel ? `${poolLabel} · ` : ""}{booking.venue_name}
                </p>
              </div>
              <StatusPill status={booking.status} />
            </div>

            <div className="my-5 h-px bg-ink/[0.18]" />

            {/* Contact details — editable */}
            {(booking.customer_first_name || booking.customer_last_name || booking.customer_email || booking.customer_phone) && (
              <>
                <div className="flex flex-col gap-3.5">
                  <EditableRow
                    label="First name"
                    value={booking.customer_first_name ?? ""}
                    onSave={(v) => updateContact("firstName", v)}
                  />
                  <EditableRow
                    label="Last name"
                    value={booking.customer_last_name ?? ""}
                    onSave={(v) => updateContact("lastName", v)}
                  />
                  {booking.customer_email && (
                    <EditableRow
                      label="Email"
                      value={booking.customer_email}
                      type="email"
                      onSave={(v) => updateContact("email", v)}
                    />
                  )}
                  {booking.customer_phone != null && (
                    <PhoneEditableRow
                      value={booking.customer_phone}
                      onSave={(v) => updateContact("phone", v)}
                    />
                  )}
                  {contactError && (
                    <p className="caption text-corten">{contactError}</p>
                  )}
                </div>
                <div className="my-5 h-px bg-ink/[0.18]" />
              </>
            )}

            <div className="flex flex-col gap-3.5">
              <BookingRow label="Date" value={nzDateLong(booking.start_at)} />
              <BookingRow
                label="Time"
                value={`${nzTime(booking.start_at)}${booking.tier_name ? ` · ${booking.tier_name}` : ""}`}
              />
              <BookingRow
                label="Guests"
                value={
                  booking.group_guests != null
                    ? `Up to ${booking.group_guests} guests`
                    : partySummary(booking.party_adults, booking.party_children)
                }
              />
              {booking.total_amount != null && (
                <BookingRow label="Paid" value={nzd(booking.total_amount)} />
              )}
            </div>
          </div>

          {/* Action zone */}
          <div className="mt-8 flex flex-col items-center gap-4">
            {booking.channel === "ota_rezdy" ? (
              <p className="caption text-center text-ink-mute max-w-md">
                This booking was made through a third-party booking platform. To change or cancel, please contact the agency you booked through directly.
              </p>
            ) : booking.agent_booked ? (
              <p className="caption text-center text-ink-mute max-w-md">
                This booking was made by {booking.agent_name || "your travel agent"}. To change or cancel it, please contact them directly.
              </p>
            ) : booking.channel === "migrated_ibis" ? (
              <p className="caption text-center text-ink-mute max-w-md">
                This booking was made through our previous booking system. To change or cancel it, please call us on +64 3 441 2588.
              </p>
            ) : cancelled != null ? (
              <p className="body text-center text-ink max-w-sm">{cancelMsg || "Booking cancelled."}</p>
            ) : isActionable ? (
              <>
                <div className="flex flex-wrap justify-center gap-3">
                  {/* Self-cancel is only offered outside the 24h window; within 24h the guest
                      must contact us. CHANGES are never self-service — a reschedule has to be
                      arranged with the team, so the guest emails in. */}
                  {!withinWindow ? (
                    <>
                      <Button variant="outline" onClick={() => setConfirmCancelOpen(true)} disabled={busy}>Cancel Booking</Button>
                      <ButtonLink
                        variant="outline"
                        href={`mailto:${booking.venue_slug === "thermae" ? "thermae" : "bathe"}@aluume.com?subject=${encodeURIComponent(`Change booking ${booking.booking_ref}`)}`}
                      >
                        Change Booking
                      </ButtonLink>
                    </>
                  ) : (
                    <ButtonLink
                      variant="outline"
                      href={`mailto:${booking.venue_slug === "thermae" ? "thermae" : "bathe"}@aluume.com?subject=${encodeURIComponent(`Booking ${booking.booking_ref}`)}`}
                    >
                      Contact us
                    </ButtonLink>
                  )}
                </div>
                {error && <p className="caption text-corten">{error}</p>}
                <button
                  onClick={resend}
                  disabled={busy || resent}
                  className="text-[12px] font-light text-ink-mute hover:text-ink transition-colors disabled:opacity-50"
                >
                  {resent ? "Confirmation sent ✓" : "Resend confirmation email"}
                </button>
              </>
            ) : null}

            {booking.channel !== "ota_rezdy" && booking.channel !== "migrated_ibis" && !booking.agent_booked && isActionable && !withinWindow && (
              <p className="caption text-center text-ink-mute max-w-md">
                Cancel free up to 24 hours before your soak — you&apos;ll get a full refund to your card. To move your booking, email us and we&apos;ll arrange it.
              </p>
            )}
          </div>
        </main>
      )}

      {/* Change booking modal */}
      {changeOpen && booking && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/50 p-0 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setChangeOpen(false); }}
        >
          <div className="flex w-full max-w-[900px] flex-col sm:flex-row rounded-t-xl sm:rounded-xl overflow-hidden max-h-[92vh]">
            {/* Left — picker */}
            <div className="flex-1 overflow-y-auto bg-bg p-8 sm:p-10 flex flex-col gap-4">
              <h3 style={{ fontFamily: "var(--font-display)" }} className="text-[28px] font-light text-ink">
                Change Your Booking
              </h3>
              <div className="h-px bg-ink/10" />

              {/* Session type toggle */}
              {hasMultipleTypes && (
                <div className="flex gap-2">
                  {(["communal", "private"] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleTypeSelect(type)}
                      className={`flex-1 rounded-pill py-2.5 text-[12px] tracking-wide uppercase transition-colors ${
                        selectedType === type
                          ? "bg-ink text-ink-on-dark font-medium"
                          : "bg-cream-deep border border-ink/20 text-ink-soft font-light"
                      }`}
                    >
                      {type === "communal" ? "Communal" : "Private Pool"}
                    </button>
                  ))}
                </div>
              )}

              <div className="h-px bg-ink/8" />

              {/* Date — 7-day chip row centered on the booking date, like the
                  customer flow; "View full calendar" opens the month picker. */}
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2.5">
                    <span className="ui-label text-mocha">Date</span>
                    <span className="text-[12px] font-light text-ink-soft">{changeMonthLabel}</span>
                  </div>
                  <button onClick={() => setShowChangeCal(true)} className="text-[12px] font-light text-mocha hover:text-ink">View full calendar</button>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {changeDates.map((d) => {
                    const on = changeDate === d;
                    const past = d < todayNZ();
                    return (
                      <button
                        key={d}
                        onClick={() => selectChangeDate(d)}
                        disabled={past}
                        className={`flex shrink-0 flex-col items-center gap-1 rounded-xl px-[14px] py-2.5 transition-colors disabled:opacity-30 ${
                          on ? "bg-ink" : "border border-ink/[0.14] bg-white hover:border-ink/40"
                        }`}
                      >
                        <span className={`text-[10px] uppercase tracking-[1.2px] ${on ? "text-ink-on-dark/70" : "text-ink-soft"}`}>{fmtChipWeekday(d)}</span>
                        <span style={{ fontFamily: "var(--font-display)" }} className={`text-[20px] leading-none ${on ? "text-ink-on-dark" : "text-ink"}`}>{fmtChipDay(d)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Private pool options — the standard categories PLUS the Remarkables
                  whole-pool hire (Bathe only). Picking Remarkables switches the flow into
                  a single-Guests, afternoon-blocks upgrade; a "Choose a standard pool" link
                  brings the standard pills back (the type toggle collapses in that mode). */}
              {selectedType === "private" && (!!remConfig || privateCategories.length > 1) && (
                <div className="flex flex-wrap items-center gap-2">
                  {isRemarkables ? (
                    <>
                      <span className="rounded-pill bg-ink px-4 py-2 text-[12px] tracking-wide uppercase text-ink-on-dark">
                        The Remarkables
                      </span>
                      <button
                        onClick={exitRemarkables}
                        className="text-[12px] font-light text-mocha hover:text-ink transition-colors"
                      >
                        Choose a standard pool
                      </button>
                    </>
                  ) : (
                    <>
                      {privateCategories.map((cat) => (
                        <button
                          key={cat}
                          onClick={() => { setSelectedCategory(cat === selectedCategory ? null : cat); setSelectedSlot(null); }}
                          className={`rounded-pill px-4 py-2 text-[12px] tracking-wide uppercase transition-colors ${
                            selectedCategory === cat
                              ? "bg-ink text-ink-on-dark"
                              : "bg-cream-deep border border-ink/20 text-ink-soft"
                          }`}
                        >
                          {cat}
                        </button>
                      ))}
                      {!!remConfig && (
                        <button
                          onClick={selectRemarkables}
                          className="rounded-pill border border-mocha/40 bg-mocha/[0.08] px-4 py-2 text-[12px] tracking-wide uppercase text-mocha transition-colors hover:bg-mocha/[0.16]"
                        >
                          The Remarkables
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Time grid */}
              {!selectedType ? (
                <p className="caption text-ink-mute">Select a session type above to see available times.</p>
              ) : slotsLoading ? (
                <p className="caption text-ink-mute">Checking availability…</p>
              ) : selectedType === "private" && !isRemarkables && !selectedCategory && (privateCategories.length > 1 || !!remConfig) ? (
                <p className="caption text-ink-mute">Select a pool above to see available times.</p>
              ) : visibleSlots.length === 0 ? (
                <p className="caption text-ink-mute">No availability on this date. Try another day.</p>
              ) : (
                <div className="flex flex-col gap-0">
                  <p className="ui-label text-mocha mb-2">Select Time</p>
                  {tierGroups.map(([tier, slots]) => (
                    <div key={tier} className="flex items-start gap-2 py-1.5">
                      <span className="w-24 shrink-0 pt-1 text-[10px] tracking-[1.2px] uppercase font-light text-ink-soft">
                        {tier}
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {slots.map((slot) => {
                          const isSel = selectedSlot?.start_at === slot.start_at && selectedSlot?.resource_id === slot.resource_id;
                          return (
                            <button
                              key={slot.resource_id + slot.start_at}
                              onClick={() => setSelectedSlot(slot)}
                              className={`rounded px-2.5 py-1.5 text-[11px] transition-colors ${
                                isSel
                                  ? "bg-ink text-ink-on-dark font-normal"
                                  : "border border-ink/18 text-ink-soft font-light hover:border-ink/50"
                              }`}
                            >
                              {nzTime(slot.start_at)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="h-px bg-ink/8" />

              {/* Remarkables requires two adults and caps adults + children at eight. */}
              <Stepper
                label="Adults"
                value={changeAdults}
                min={isRemarkables ? REM_MIN_PARTY : 1}
                max={isRemarkables ? REM_MAX_PARTY - changeChildren : undefined}
                onChange={(d) => adjustParty("adults", d)}
              />
              {/* The Commons is adults-only (16+) — Children only shows for private
                  pools at venues that allow children (Bathe yes, Thermae no), and never
                  for a Remarkables whole-pool hire (also adults-only). */}
              {selectedType === "private" && venueAllowsChildren && (
                <>
                  <div className="h-px bg-ink/8" />
                  <Stepper label="Children" value={changeChildren} min={0} max={isRemarkables ? REM_MAX_PARTY - changeAdults : undefined} onChange={(d) => adjustParty("children", d)} />
                </>
              )}
            </div>

            {/* Right — summary */}
            <div className="w-full sm:w-80 shrink-0 bg-bg-elevated overflow-y-auto p-7 flex flex-col">
              <p className="ui-label text-mocha">Summary of Changes</p>

              {/* Current booking */}
              <div className="mt-5 rounded-md border border-ink/12 p-4 flex flex-col gap-1">
                <p className="text-[9px] tracking-[1.3px] uppercase font-light text-ink-soft">Current</p>
                <div className="h-1.5" />
                <p className="text-[14px] font-normal text-ink">{categoryLabel}</p>
                {poolLabel && (
                  <p className="text-[11px] font-light text-ink-soft">{poolLabel}</p>
                )}
                <p className="text-[12px] font-light text-ink-soft">
                  {nzDateShort(booking.start_at)}
                  {booking.tier_name ? ` · ${booking.tier_name}` : ""}
                  {` · ${nzTime(booking.start_at)}`}
                </p>
                <p className="text-[12px] font-light text-ink-soft">
                  {partyLabel(booking.party_adults, booking.party_children, origIsRemarkables)}
                  {booking.total_amount != null && <> · {nzd(booking.total_amount)}</>}
                </p>
              </div>

              <p className="my-3 text-center text-[20px] text-ink-mute">↓</p>

              {/* New slot */}
              <div className={`rounded-md border p-4 flex flex-col gap-1 transition-opacity ${
                selectedSlot ? "bg-mocha/[0.12] border-mocha/50" : "border-ink/12 opacity-40"
              }`}>
                <p className="text-[9px] tracking-[1.3px] uppercase font-light text-mocha">New</p>
                <div className="h-1.5" />
                {selectedSlot ? (
                  <>
                    <p className="text-[14px] font-normal text-ink">
                      {isRemarkables ? REMARKABLES_PRODUCT_NAME : selectedType === "communal" ? "Communal" : selectedSlot.resource_name}
                    </p>
                    <p className="text-[12px] font-light text-ink-soft">
                      {nzDateShort(selectedSlot.start_at)}
                      {selectedSlot.tier_name ? ` · ${selectedSlot.tier_name}` : ""}
                      {` · ${nzTime(selectedSlot.start_at)}`}
                    </p>
                    <p className="text-[12px] font-light text-ink-soft">
                      {partyLabel(changeAdults, changeChildren, isRemarkables)}
                      {newTotal != null && <> · {nzd(newTotal)}</>}
                    </p>
                  </>
                ) : (
                  <p className="text-[12px] font-light text-ink-mute">Select a time above</p>
                )}
              </div>

              {selectedSlot && isComp && (
                <div className="mt-4">
                  <div className="h-px bg-ink/10" />
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[13px] font-light text-ink-soft">Complimentary</span>
                    <span className="text-[15px] font-medium text-ink">No charge</span>
                  </div>
                  <div className="mt-2 rounded bg-corten/[0.08] px-3 py-2.5">
                    <p className="text-[11px] font-light text-ink-soft">
                      This is a complimentary booking — rescheduling keeps it free.
                    </p>
                  </div>
                </div>
              )}

              {selectedSlot && !isComp && priceDiff != null && Math.abs(priceDiff) > 0.01 && (
                <div className="mt-4">
                  <div className="h-px bg-ink/10" />
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-[13px] font-light text-ink-soft">
                      {priceDiff > 0 ? "Amount to pay" : "Refund due"}
                    </span>
                    <span className={`text-[15px] font-medium ${priceDiff > 0 ? "text-corten" : "text-ink"}`}>
                      {priceDiff > 0 ? "" : "−"}{nzd(Math.abs(priceDiff))}
                    </span>
                  </div>
                  <div className="mt-2 rounded bg-corten/[0.08] px-3 py-2.5">
                    <p className="text-[11px] font-light text-ink-soft">
                      {priceDiff > 0
                        ? `Pay ${nzd(priceDiff)} now to confirm your new time.`
                        : `${nzd(-priceDiff)} will be refunded to your card.`}
                    </p>
                  </div>
                </div>
              )}

              {error && <p className="caption mt-3 text-corten">{error}</p>}

              <div className="mt-auto pt-6 flex flex-col gap-3">
                <Button
                  onClick={confirmChange}
                  loading={changing}
                  disabled={!selectedSlot}
                  className="w-full"
                >
                  {changing ? "Confirming…" : priceDiff != null && priceDiff > 0.01 ? `Pay ${nzd(priceDiff)} & Confirm` : "Confirm Change"}
                </Button>
                <button
                  onClick={() => setChangeOpen(false)}
                  className="w-full text-center text-[12px] font-light text-ink-soft hover:text-ink transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full-calendar popup for the change modal */}
      {showChangeCal && (
        <Calendar
          value={changeDate}
          onSelect={(d) => { recenterChange(d); setShowChangeCal(false); }}
          onClose={() => setShowChangeCal(false)}
        />
      )}

      {/* Cancel confirmation — centered, Aluume-branded (replaces window.confirm) */}
      {confirmCancelOpen && booking && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/50 p-6"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmCancelOpen(false); }}
        >
          <div className="w-full max-w-[400px] rounded-2xl bg-cream p-8 text-center shadow-[0px_24px_60px_-20px_rgba(57,51,48,0.45)]">
            <p className="ui-label text-ink-mute">Aluume · #{booking.booking_ref}</p>
            <h3 style={{ fontFamily: "var(--font-display)" }} className="mt-3 text-[26px] font-light leading-tight text-ink">
              Cancel this booking?
            </h3>
            <p className="body mt-3 text-ink-soft">
              {withinWindow
                ? "Within 24 hours we can’t refund, but we’ll email you a voucher for the full amount (valid 3 years) to use any time. This can’t be undone."
                : "You’ll receive a full refund to your original card (a few business days to appear). This can’t be undone."}
            </p>
            <label className="mt-5 flex flex-col gap-1.5 text-left">
              <span className="ui-label text-ink-mute">Reason for cancellation (optional)</span>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                placeholder="Let us know why — this helps us improve."
                className="resize-none border-b border-ink/30 bg-transparent py-2 text-[13px] text-ink outline-none focus:border-ink"
              />
            </label>
            <div className="mt-7 flex flex-col gap-3">
              <Button variant="outline" onClick={doCancel} loading={busy} className="w-full">
                {busy ? "Cancelling…" : "Yes, cancel booking"}
              </Button>
              <button
                onClick={() => setConfirmCancelOpen(false)}
                className="text-[12px] font-light text-ink-soft hover:text-ink transition-colors"
              >
                Keep my booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Difference-payment Drop-In (live providers only; inert on staging) */}
      {livePay && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/55 p-6">
          <div className="w-full max-w-[460px] rounded-2xl bg-cream p-8 shadow-[0px_24px_60px_-20px_rgba(57,51,48,0.45)]">
            <h3 style={{ fontFamily: "var(--font-display)" }} className="text-[24px] font-light text-ink">
              Complete payment
            </h3>
            <p className="body mt-2 text-ink-soft">
              {payConfirming
                ? "Payment received — confirming your new time…"
                : `Pay ${nzd(livePay.diffCents / 100)} to confirm your new time.`}
            </p>
            {payConfirming ? (
              <div className="mt-8 mb-2 flex flex-col items-center justify-center gap-3">
                <div className="h-7 w-7 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
                <p className="text-[13px] font-light text-ink-soft">Just a moment…</p>
              </div>
            ) : (
              <>
                <div className="mt-6">
                  <VerifonePaymentFrame
                    iframeUrl={livePay.iframeUrl}
                    onScriptError={() => setPayError("The payment form couldn't load. Please try again.")}
                  />
                </div>
                <div className="mt-4">
                  <PaymentInfoNote />
                </div>
                {payError && <p className="caption mt-4 text-corten">{payError}</p>}
                <button
                  onClick={() => { setLivePay(null); setPayError(""); }}
                  className="mt-6 w-full text-center text-[12px] font-light text-ink-soft hover:text-ink transition-colors"
                >
                  Cancel payment
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}

export default function ManagePage() {
  return (
    <Suspense fallback={<main className="p-12 text-center text-ink-mute">Loading…</main>}>
      <ManageInner />
    </Suspense>
  );
}
